import * as STD from '@emersya/mpa-std';
import type TPrice from './Price';
import type TProduct from './Product';
import type TPart from './Part';
import type {Option as TOption} from './Option';

abstract class Pricing<TType extends string/*, TSerialized extends Pricing.ISerialized*/> {
	static deserialize(serialized: Pricing.FinalPricing.ISerialized): Pricing.FinalPricing;
	static deserialize(serialized: Pricing.BoundPricing.ISerialized): Pricing.BoundPricing;
	static deserialize<TSerialized extends Pricing.TPricingSerialized>(serialized: TSerialized): Pricing.TPricing {
		if (serialized.type === 'final') {
			return Pricing.FinalPricing.protectedDeserialize(serialized);
		}
		return Pricing.BoundPricing.protectedDeserialize(serialized);
	};

	readonly type: TType;

	abstract resolve(): Promise<TPrice.FinalPrice>;

	abstract serialize(): Pricing.ISerialized<TType>;

	duplicate() {
		return Pricing.deserialize(<Pricing.FinalPricing.ISerialized & Pricing.BoundPricing.ISerialized>this.serialize());
	};

	protected constructor(type: TType) {
		this.type = type;
	};
};

namespace Pricing {
	export interface ISerialized<TType extends string> {
		readonly type: TType;
	};

	export class FinalPricing extends Pricing<'final'> {

		static protectedDeserialize(serialized: Pricing.FinalPricing.ISerialized): Pricing.FinalPricing {
			return new this(window.BasketNS.Price.deserialize(serialized.price));
		};

		private readonly price: TPrice.FinalPrice;

		async resolve() {
			return await Promise.resolve(this.price);
		};

		serialize() {
			return {
				type: this.type,
				price: this.price.serialize()
			};
		};

		constructor(price: TPrice) {
			super('final');
			this.price = price;
		};
	};
	export namespace FinalPricing {
		export interface ISerialized extends Pricing.ISerialized<'final'> {
			readonly price: TPrice.ISerialized;
		};
		// export type TSerialized = Pricing.ISerialized & Price.ISerialized;
	}

	export class BoundPricing extends Pricing<'bound'> {
		static protectedDeserialize(serialized: Pricing.BoundPricing.ISerialized): Pricing.BoundPricing {
			return new this(
				serialized.boundTo,
				Object.entries(serialized.prices).reduce<Record<string, Pricing.TPricing>>((a, c) => {
					// Ts narrow system does not allow doing it without conditions...
					if (c[1].type === 'final') {
						a[c[0]] = Pricing.deserialize(c[1]);
					} else if (c[1].type === 'bound') {
						a[c[0]] = Pricing.deserialize(c[1]);
					}
					return a;
				}, {})
			);
		};

		static resolve(
			pricing: BoundPricing,
			host: TProduct | TPart | TOption | BoundPricing
		) {
			const _ = Object.create(pricing);
			Object.defineProperty(_, 'host', {writable: true, value: host});
			return pricing.resolve.call(_);
		}

		private readonly boundTo: string;
		private readonly getBinding: (host: TProduct | TPart | TOption) => Promise<string>;
		private readonly prices: Record<string, TPricing>;

		_host!: TProduct | TPart | TOption | BoundPricing;

		get host(): TProduct | TPart | TOption {
			if (this._host instanceof BoundPricing) {
				return this._host.host;
			}
			return this._host;
		};

		serialize(): Pricing.BoundPricing.ISerialized {
			return {
				type: this.type,
				boundTo: this.boundTo,
				prices: STD.object.fromKeyValueList(Object.entries(this.prices).map(x => [x[0], x[1].serialize()]))
			};
		};

		async resolve(): Promise<TPrice.FinalPrice> {
			const key = await this.getBinding(this.host);
			if (!key) {
				throw new Error;
			}
			const price = this.prices[key];
			if (!price) {
				throw new Error;
			}
			return price.resolve();
		};

		constructor(
			boundTo: string,
			prices: Record<string, TPricing>
		) {
			super('bound');
			this.boundTo = boundTo;
			this.getBinding = window.BasketNS.Basket.safeApp.globalPricingContext.generatePricingGetBinding(boundTo);
			this.prices = prices;
			for (const pricing of Object.values(prices)) {
				if (pricing instanceof BoundPricing) {
					pricing._host = this;
				}
			}
		};

	};

	export namespace BoundPricing {
		export interface ISerialized extends Pricing.ISerialized<'bound'> {
			readonly boundTo: string;
			readonly prices: Record<string, Pricing.TPricingSerialized>;
		};
	}
	export type TPricing = FinalPricing | BoundPricing;
	export type TPricingSerialized = FinalPricing.ISerialized | BoundPricing.ISerialized;
}
export default Pricing;
